import { Game } from "@/store/types";

/**
 * The choices in court setup. Each change adjusts the others: picking courts
 * or a format resets the player count to fill them, and removing players
 * releases courts nobody would play on. The iOS app follows the same rules
 * (CardsKit's GameSetup).
 */
export interface GameSetup {
  courts: number[];
  players: number;
  perCourt: number;
  shortCourt?: number;
}

export const MIN_PLAYERS = 2;

export const setupOf = (game: Game): GameSetup => ({
  courts: game.courts ?? [],
  players: game.players ?? 0,
  perCourt: game.perCourt ?? 4,
  shortCourt: game.shortCourt,
});

/** Full courts plus one short of another full court; more would need
 * another court. */
export const maxPlayers = (setup: GameSetup) =>
  setup.courts.length * setup.perCourt + (setup.perCourt - 1);

export const canSave = (setup: GameSetup) =>
  setup.players > 0 && setup.perCourt > 0;

/** Players on the court that isn't full, or 0 when every court is. */
export const shortCourtPlayers = ({ courts, players, perCourt }: GameSetup) =>
  perCourt > 0 && players < courts.length * perCourt ? players % perCourt : 0;

/** Whether to ask which court takes the leftover players. */
export const choosesShortCourt = (setup: GameSetup) =>
  shortCourtPlayers(setup) > 0 && setup.courts.length > 1;

export const shortCourtTitle = (setup: GameSetup) => {
  switch (shortCourtPlayers(setup)) {
    case 1:
      return "Lone Court";
    case 2:
      return "Singles Court";
    case 3:
      return "3 Player Court";
    default:
      return "Extras";
  }
};

/** Switches between singles (2) and doubles (4). */
export const setFormat = (setup: GameSetup, perCourt: number) =>
  normalizeShortCourt({
    ...setup,
    perCourt,
    players: setup.courts.length
      ? setup.courts.length * perCourt
      : setup.players,
  });

export const toggleCourt = (setup: GameSetup, court: number) => {
  const courts = setup.courts.includes(court)
    ? setup.courts.filter((c) => c !== court)
    : [...setup.courts, court].sort((a, b) => a - b);
  return normalizeShortCourt({
    ...setup,
    courts,
    players: courts.length * setup.perCourt,
  });
};

/** Too few players for doubles switches to singles, and courts left empty
 * are released, highest numbers first. */
const setPlayers = (setup: GameSetup, players: number) => {
  const perCourt = players < 3 && setup.perCourt === 4 ? 2 : setup.perCourt;
  const emptyCourts = Math.floor(
    (setup.courts.length * perCourt - players) / perCourt,
  );
  const courts =
    emptyCourts > 0
      ? setup.courts.slice(0, Math.max(setup.courts.length - emptyCourts, 0))
      : setup.courts;
  return normalizeShortCourt({ ...setup, courts, players, perCourt });
};

export const addPlayer = (setup: GameSetup) =>
  setup.players < maxPlayers(setup)
    ? setPlayers(setup, setup.players + 1)
    : setup;

export const removePlayer = (setup: GameSetup) =>
  setup.players > MIN_PLAYERS ? setPlayers(setup, setup.players - 1) : setup;

export const selectShortCourt = (setup: GameSetup, shortCourt: number) => ({
  ...setup,
  shortCourt,
});

/** Picks a short court, the highest numbered, when one is needed and the
 * current pick is no longer in play. */
const normalizeShortCourt = (setup: GameSetup): GameSetup => {
  const { courts, players, perCourt, shortCourt } = setup;
  if (players >= courts.length * perCourt || courts.length <= 1) return setup;
  if (shortCourt !== undefined && courts.includes(shortCourt)) return setup;
  return { ...setup, shortCourt: Math.max(...courts) };
};
